import React from "react";
import { MdPeopleOutline } from "react-icons/md";
import { CapacityCell, CapacityTimelineResponse } from "../../../../interfaces/Capacity";

interface Props {
  data: CapacityTimelineResponse | null;
}

const cellTone = (c: CapacityCell) => {
  if (c.allocatedHours === 0 && c.workedHours === 0) return "bg-gray-50 dark:bg-navy-900";
  if (c.utilizationColor === "red") return "bg-red-100 dark:bg-red-900/30";
  if (c.utilizationColor === "yellow") return "bg-amber-100 dark:bg-amber-900/30";
  return "bg-green-100 dark:bg-green-900/30";
};

const barTone = (color: string) =>
  color === "red" ? "bg-red-500" : color === "yellow" ? "bg-amber-500" : "bg-green-500";

const fmtWeek = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
};

const buildTitle = (c: CapacityCell) => {
  const lines = [
    `Capacity: ${c.capacityHours}h`,
    `Alocado: ${c.allocatedHours}h (${c.utilizationPct}%)`,
    `Estimado (backlog): ${c.estimatedHours}h`,
    `Trabalhado: ${c.workedHours}h`,
  ];
  if (c.projects.length) {
    lines.push("—");
    c.projects.forEach((p) =>
      lines.push(
        `${p.projectName}: ${p.allocatedHours}h aloc / ${p.estimatedHours}h est / ${p.workedHours}h trab`
      )
    );
  }
  return lines.join("\n");
};

const CapacityTimeline: React.FC<Props> = ({ data }) => {
  if (!data) {
    return (
      <div className="py-10 text-center text-sm text-gray-700 dark:text-gray-300">Carregando...</div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-[760px] border-separate border-spacing-1">
        <thead>
          <tr>
            <th className="sticky left-0 z-10 bg-white px-2 text-left text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:bg-navy-800 dark:text-gray-300">
              Colaborador
            </th>
            {data.weeks.map((w) => (
              <th key={w} className="px-1 text-center text-[10px] font-medium text-gray-600 dark:text-gray-300">
                {fmtWeek(w)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((row) => (
            <tr key={row.employeeId}>
              <td className="sticky left-0 z-10 bg-white px-2 dark:bg-navy-800">
                <p className="text-xs font-semibold text-navy-700 dark:text-white">
                  {row.employeeName}
                </p>
                <p className="text-[10px] text-gray-500 dark:text-gray-400">{row.weeklyCapacityHours}h/sem</p>
              </td>
              {row.cells.map((c) => (
                <td key={c.weekStart} className="p-0">
                  <div
                    className={`flex h-12 w-16 flex-col items-center justify-center rounded-md ${cellTone(c)} ${
                      c.hasOverride ? "ring-1 ring-dv-blue-400" : ""
                    }`}
                    title={buildTitle(c)}
                  >
                    <span className="text-[11px] font-semibold text-navy-700 dark:text-white">
                      {c.allocatedHours}/{c.capacityHours}
                    </span>
                    <div className="mt-1 h-1 w-12 overflow-hidden rounded-full bg-white/60 dark:bg-navy-900">
                      <div
                        className={`h-full ${barTone(c.utilizationColor)}`}
                        style={{ width: `${Math.min(100, c.utilizationPct)}%` }}
                      />
                    </div>
                    {c.estimatedHours > 0 && (
                      <span className="mt-0.5 text-[9px] font-medium text-dv-blue-600 dark:text-dv-cyan-300">
                        ~{c.estimatedHours}h est
                      </span>
                    )}
                  </div>
                </td>
              ))}
            </tr>
          ))}
          {data.rows.length === 0 && (
            <tr>
              <td className="px-3 py-10 text-center text-gray-700 dark:text-gray-300" colSpan={data.weeks.length + 1}>
                <span className="flex flex-col items-center gap-2">
                  <MdPeopleOutline aria-hidden="true" className="h-8 w-8 text-gray-400 dark:text-gray-500" />
                  Nenhum colaborador ativo.
                </span>
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="mt-3 flex flex-wrap gap-3 text-[11px] text-gray-700 dark:text-gray-300">
        <span className="flex items-center gap-1"><i aria-hidden="true" className="h-2.5 w-2.5 rounded-sm bg-green-500" /> &lt; 80%</span>
        <span className="flex items-center gap-1"><i aria-hidden="true" className="h-2.5 w-2.5 rounded-sm bg-amber-500" /> 80–99%</span>
        <span className="flex items-center gap-1"><i aria-hidden="true" className="h-2.5 w-2.5 rounded-sm bg-red-500" /> ≥ 100% (sobre-alocado)</span>
        <span className="flex items-center gap-1"><i aria-hidden="true" className="h-2.5 w-2.5 rounded-sm ring-1 ring-dv-blue-400" /> capacity ajustada (override)</span>
      </div>
      <p className="mt-1 text-[11px] text-gray-700 dark:text-gray-300">
        Cada célula: horas alocadas / capacity da semana. Passe o mouse para ver o detalhe por projeto e as horas trabalhadas (timesheet).
      </p>
    </div>
  );
};

export default CapacityTimeline;
